import React, { createContext, useState, useCallback, useEffect } from "react";

export const AuthContext = createContext(null);

const TOKEN_KEY  = "gs_token";
const ACCOUNT_KEY = "gs_account_id";

// Decode the token (base64 → "accountId.timestamp.sig") and check TTL.
// Token TTL is enforced server-side; we do a soft client-side check of 1 hour.
function isTokenExpired(token) {
  try {
    const decoded = atob(token);
    const parts = decoded.split(".");
    if (parts.length < 2) return true;
    const ts = parseInt(parts[1], 10);
    return Date.now() / 1000 - ts > 3600;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken]       = useState(() => localStorage.getItem(TOKEN_KEY));
  const [accountId, setAccountId] = useState(() => localStorage.getItem(ACCOUNT_KEY));

  // Clear stale tokens on mount
  useEffect(() => {
    if (token && isTokenExpired(token)) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACCOUNT_KEY);
      setToken(null);
      setAccountId(null);
    }
  }, [token]);

  const login = useCallback((newToken, newAccountId) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(ACCOUNT_KEY, newAccountId);
    setToken(newToken);
    setAccountId(newAccountId);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    localStorage.removeItem("gs_is_admin");
    setToken(null);
    setAccountId(null);
  }, []);

  const isAuthenticated = Boolean(token && !isTokenExpired(token));

  return (
    <AuthContext.Provider value={{ token, accountId, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
